// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import { useEffect } from 'react';
import { useSessionGuardStore } from '../stores/sessionGuardStore';
import * as authService from '../services/authService';
import { stashReturnPath } from '../utils/returnPath';

export const ABSOLUTE_SESSION_MS = 12 * 60 * 60 * 1000; // 12h

/**
 * Hard sign-out 12h after sign-in, regardless of activity — no warning
 * modal, straight to the full sign-in screen (ADO-25800). A 1-minute tick is
 * plenty of granularity for a 12-hour window; no need for the idle-warning's
 * 1s resolution here.
 *
 * Skipped while `locked`, same as `useIdleWarning`'s tick: `LockOverlay`'s
 * "Unlock" is already mid `msalInstance.loginPopup()` at that point, and
 * `logout()`'s `logoutRedirect()` colliding with that in-flight interaction
 * throws MSAL's `interaction_in_progress` and strands the popup instead of
 * cleanly signing out. The overlay already blocks all access and forces a
 * fresh interactive re-auth to dismiss, so the 12h guarantee isn't weakened
 * by waiting for it to resolve first — the tick fires again as soon as
 * `locked` clears, and signs out immediately then if still over the limit
 * (`unlock()` doesn't reset `signInAt`).
 */
export function useAbsoluteSessionLimit(enabled: boolean): void {
  useEffect(() => {
    if (!enabled) return undefined;

    const tick = () => {
      const { signInAt, locked } = useSessionGuardStore.getState();
      if (locked) return;
      if (signInAt && Date.now() - signInAt >= ABSOLUTE_SESSION_MS) {
        useSessionGuardStore.getState().clearSignInAt();
        stashReturnPath(window.location.pathname + window.location.search);
        void authService.logout();
      }
    };

    const id = setInterval(tick, 60_000);
    return () => clearInterval(id);
  }, [enabled]);
}
